import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    compactParams,
    type DataTableFilterMap,
    type DataTableFilterValue,
    type DataTableParams
} from './params';
import type { DataTableMode, SortOrder } from './types';

/**
 * Table state, and the one canonical params object every endpoint is asked
 * with. See `params.ts` for the contract itself.
 *
 * What this hook is: `page`/`limit`/`sort`/`order`/`search`/`filters`, the
 * page-reset rules that every table in this repo currently re-implements by
 * hand, opt-in `sessionStorage` persistence, and `params`.
 *
 * What it is emphatically not: a data layer. It never fetches, never
 * debounces, and has no idea TanStack Query exists. The app owns the query,
 * the debounce and - in `'infinite'` mode - the accumulated rows.
 */

/** Chunk size used in `'infinite'` mode when none is given. */
const DEFAULT_INFINITE_LIMIT = 50;
const DEFAULT_PAGED_LIMIT = 20;
/**
 * The load-more list is the one every table gets unless it opts out: a
 * pagination footer is one more click for no benefit once the data layer
 * supports accumulating chunks. A table that isn't wired for it yet passes
 * `mode: 'paged'` explicitly rather than relying on this default.
 */
const DEFAULT_MODE: DataTableMode = 'infinite';

export interface DataTableSortState<K extends string> {
    by: K;
    order: SortOrder;
}

export interface UseDataTableOptions<
    K extends string = string,
    F extends DataTableFilterMap = DataTableFilterMap
> {
    /**
     * Sort applied before the user touches a header. Omit it for a table with
     * no sortable columns (the calls list, say): `sort`/`order` then never
     * appear in `params`, so the endpoint is never asked for an ordering it
     * doesn't support.
     */
    defaultSort?: DataTableSortState<K>;
    /** Page size in `'paged'` mode. Defaults to 20. */
    defaultLimit?: number;
    /**
     * Starting filter values. Every filter the table uses should appear here,
     * even as `[]` or `null` - the key set decides what gets restored from
     * storage and what `reset()` returns to.
     */
    defaultFilters?: F;
    /** Starting mode. Defaults to `'infinite'`. */
    mode?: DataTableMode;
    /**
     * Page size in `'infinite'` mode. Fixed and deliberately larger than a
     * paged limit: a viewport-fit size is meaningless when the list grows past
     * the viewport by design.
     *
     * This is also the guard against the two sizing mechanisms fighting. In
     * `'infinite'` mode `limit` is *locked* to this value and `setLimit` is a
     * no-op, so an app that wires `useViewportPageSize` straight into
     * `setLimit` cannot shrink the chunk size out from under an infinite list.
     * Defaults to 50.
     */
    infiniteLimit?: number;
    /**
     * Opt in to persistence. Off by default. When set, sort, filters and mode
     * (never `page`) are stored under this one `sessionStorage` key as
     * `{ sort, order, filters, mode }`.
     *
     * Mode rides along with sort/filters on purpose: it is the same kind of
     * thing - a per-table display preference, not row data - so a table that
     * already opts into persisting one opts into persisting all three. A
     * table without a `storageKey` gets `mode`'s option default (see
     * `UseDataTableOptions.mode`) fresh on every mount, same as it always got
     * `defaultSort` fresh.
     */
    storageKey?: string;
    /**
     * Per-filter `sessionStorage` keys, as `{ filterName: storageKey }`. Each
     * listed filter is stored alone, under exactly that key, as the bare JSON
     * value - which is the format the pages that hand-rolled this already use
     * (`fv_accounts_practiceInvoiceStatusFilter` and friends), so a migrating
     * table keeps its users' restored state.
     *
     * Independent of `storageKey`: use it alone to persist filters without
     * persisting sort, or alongside, in which case the filters listed here are
     * left out of the `storageKey` blob. Omit a filter to stop persisting it -
     * e.g. a practice filter that is irrelevant on a practice-scoped page.
     */
    filterStorageKeys?: Readonly<Record<string, string>>;
    /**
     * Order to apply when sorting switches to a *different* column. Defaults
     * to ascending. Tables where "biggest/newest first" is the useful default
     * override per column, e.g.
     * `(by) => (by === 'due' || by === 'patient' ? 'asc' : 'desc')`.
     */
    defaultOrderFor?: (by: K) => SortOrder;
}

export interface UseDataTableResult<
    K extends string = string,
    F extends DataTableFilterMap = DataTableFilterMap
> {
    page: number;
    /** In `'infinite'` mode this is `infiniteLimit`, not the paged limit. */
    limit: number;
    /** The sorted column's key. */
    sort: K;
    order: SortOrder;
    /** The committed search term. Debounce upstream of `setSearch`. */
    search: string;
    filters: F;
    mode: DataTableMode;
    /**
     * `{ page, limit, sort, order, search?, ...filters }` with empty values
     * dropped. Stable between renders that don't change the state it derives
     * from, so it is safe in a query key.
     */
    params: DataTableParams<K>;
    setPage: (page: number) => void;
    /** Ignored in `'infinite'` mode - see `infiniteLimit`. */
    setLimit: (limit: number) => void;
    /** Same column flips the order; a new column starts at `defaultOrderFor`. */
    toggleSort: (by: K) => void;
    setSearch: (search: string) => void;
    setFilter: <N extends keyof F & string>(name: N, value: F[N]) => void;
    setMode: (mode: DataTableMode) => void;
    /** Back to the defaults, page included. Persisted entries are rewritten. */
    reset: () => void;
}

/**
 * What `useDataTable` returns when `defaultSort` is omitted: no `sort`/`order`
 * (they also never appear in `params`), and `toggleSort` is a typed-away no-op
 * so a sortless table can't accidentally wire a header to it.
 */
export interface UseDataTableUnsortedResult<
    F extends DataTableFilterMap = DataTableFilterMap
> extends Omit<UseDataTableResult<string, F>, 'sort' | 'order' | 'toggleSort'> {
    sort: undefined;
    order: undefined;
    toggleSort: (by: never) => void;
}

interface TableState<K extends string, F extends DataTableFilterMap> {
    page: number;
    limit: number;
    sort: K | undefined;
    order: SortOrder | undefined;
    search: string;
    filters: F;
    mode: DataTableMode;
}

interface PersistedState<K extends string> {
    sort?: K;
    order?: SortOrder;
    filters?: DataTableFilterMap;
    mode?: DataTableMode;
}

function readStored(key: string): unknown {
    try {
        const raw = sessionStorage.getItem(key);
        return raw === null ? undefined : JSON.parse(raw);
    } catch {
        // A disabled/full sessionStorage or a stale malformed entry must never
        // stop a table rendering - fall back to the defaults.
        return undefined;
    }
}

function writeStored(key: string, value: unknown): void {
    try {
        sessionStorage.setItem(key, JSON.stringify(value));
    } catch {
        /* Persistence is a convenience; a quota error is not an app error. */
    }
}

function isSortOrder(value: unknown): value is SortOrder {
    return value === 'asc' || value === 'desc';
}

function isDataTableMode(value: unknown): value is DataTableMode {
    return value === 'paged' || value === 'infinite';
}

function isPersistedState<K extends string>(value: unknown): value is PersistedState<K> {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/** Shallow equality, with arrays compared element-wise. */
function sameFilterValue(a: DataTableFilterValue, b: DataTableFilterValue): boolean {
    if (Array.isArray(a) && Array.isArray(b)) {
        return a.length === b.length && a.every((item, i) => item === b[i]);
    }
    return a === b;
}

/**
 * A stored value is only trusted when it is the same *kind* of thing as the
 * default it would replace - a list stays a list, a scalar stays a scalar.
 * `sessionStorage` outlives deploys, and a filter that used to be single-select
 * must not come back as a string where the UI now expects an array.
 */
function isUsableStoredValue(stored: unknown, fallback: DataTableFilterValue): boolean {
    if (stored === undefined) {
        return false;
    }
    if (Array.isArray(fallback)) {
        return Array.isArray(stored) && stored.every((item) => typeof item === 'string');
    }
    // A single-select default is usually `null`, which says nothing about the
    // value's type - any scalar is fine, a list is not.
    return (
        stored === null ||
        typeof stored === 'string' ||
        typeof stored === 'number' ||
        typeof stored === 'boolean'
    );
}

/**
 * Rebuilds the filter map from storage. Only keys present in `defaults` are
 * considered, so a renamed or retired filter left behind in `sessionStorage`
 * is ignored rather than re-introduced.
 */
function restoreFilters<F extends DataTableFilterMap>(
    defaults: F,
    blobFilters: DataTableFilterMap | undefined,
    filterStorageKeys: Readonly<Record<string, string>> | undefined
): F {
    const restored = Object.fromEntries(
        Object.keys(defaults).map((name) => {
            const ownKey = filterStorageKeys?.[name];
            const stored = ownKey ? readStored(ownKey) : blobFilters?.[name];
            return [name, isUsableStoredValue(stored, defaults[name]) ? stored : defaults[name]];
        })
    );
    // The map is rebuilt key-for-key from `defaults`, so it has `F`'s shape;
    // `Object.fromEntries` can only ever type that as a plain record.
    return restored as F;
}

function createInitialState<K extends string, F extends DataTableFilterMap>(
    options: UseDataTableOptions<K, F>,
    defaultFilters: F
): TableState<K, F> {
    const {
        defaultSort,
        defaultLimit,
        storageKey,
        filterStorageKeys,
        mode = DEFAULT_MODE
    } = options;
    const blob = storageKey ? readStored(storageKey) : undefined;
    const persisted = isPersistedState<K>(blob) ? blob : undefined;
    return {
        page: 1,
        limit: defaultLimit ?? DEFAULT_PAGED_LIMIT,
        // A sortless table ignores any persisted sort - a stale entry from a
        // deploy when the table *was* sortable must not resurrect an ordering
        // the endpoint no longer gets asked for.
        sort: defaultSort && typeof persisted?.sort === 'string' ? persisted.sort : defaultSort?.by,
        order: defaultSort && isSortOrder(persisted?.order) ? persisted.order : defaultSort?.order,
        search: '',
        filters: restoreFilters(defaultFilters, persisted?.filters, filterStorageKeys),
        mode: isDataTableMode(persisted?.mode) ? persisted.mode : mode
    };
}

const NO_FILTERS = {} as const;

// Two call shapes, kept apart by overloads: supplying `defaultSort` keeps the
// strict `K` typing on `sort`/`toggleSort` exactly as before, while omitting it
// returns the unsorted result without forcing the caller to name a `K` at all.
export function useDataTable<
    K extends string = string,
    F extends DataTableFilterMap = DataTableFilterMap
>(
    options: UseDataTableOptions<K, F> & { defaultSort: DataTableSortState<K> }
): UseDataTableResult<K, F>;
export function useDataTable<F extends DataTableFilterMap = DataTableFilterMap>(
    options: Omit<UseDataTableOptions<string, F>, 'defaultSort' | 'defaultOrderFor'> & {
        defaultSort?: undefined;
    }
): UseDataTableUnsortedResult<F>;
export function useDataTable<
    K extends string = string,
    F extends DataTableFilterMap = DataTableFilterMap
>(options: UseDataTableOptions<K, F>): UseDataTableResult<K, F> | UseDataTableUnsortedResult<F> {
    const { infiniteLimit = DEFAULT_INFINITE_LIMIT, storageKey, filterStorageKeys } = options;

    // `defaultFilters` is typically a fresh object literal every render, so the
    // identity is useless as a dependency - the value is read through a ref and
    // the initial state is built once.
    const defaultFilters = options.defaultFilters ?? (NO_FILTERS as F);
    const optionsRef = useRef(options);
    optionsRef.current = options;
    const defaultFiltersRef = useRef(defaultFilters);
    defaultFiltersRef.current = defaultFilters;

    const [state, setState] = useState<TableState<K, F>>(() =>
        createInitialState(options, defaultFilters)
    );

    const setPage = useCallback((page: number) => {
        setState((prev) => (prev.page === page ? prev : { ...prev, page }));
    }, []);

    const setLimit = useCallback((limit: number) => {
        setState((prev) => {
            // Locked in infinite mode: `limit` is `infiniteLimit` there, and a
            // viewport-fit page size has nothing to say about a growing list.
            if (prev.mode === 'infinite' || prev.limit === limit) {
                return prev;
            }
            // Page N at the old size points at different rows at the new one.
            return { ...prev, limit, page: 1 };
        });
    }, []);

    const toggleSort = useCallback((by: K) => {
        setState((prev) => {
            // A sortless table (no `defaultSort`) has nothing to toggle - the
            // unsorted overload types this away, this is the runtime backstop.
            if (!optionsRef.current.defaultSort) {
                return prev;
            }
            const order: SortOrder =
                prev.sort === by
                    ? prev.order === 'asc'
                        ? 'desc'
                        : 'asc'
                    : (optionsRef.current.defaultOrderFor?.(by) ?? 'asc');
            return { ...prev, sort: by, order, page: 1 };
        });
    }, []);

    const setSearch = useCallback((search: string) => {
        setState((prev) => (prev.search === search ? prev : { ...prev, search, page: 1 }));
    }, []);

    const setFilter = useCallback(<N extends keyof F & string>(name: N, value: F[N]) => {
        setState((prev) => {
            if (sameFilterValue(prev.filters[name], value)) {
                return prev;
            }
            // Spreading `F` and overwriting one of its own keys yields `F`;
            // a computed key is all TypeScript sees.
            const filters = { ...prev.filters, [name]: value } as F;
            return { ...prev, filters, page: 1 };
        });
    }, []);

    const setMode = useCallback((mode: DataTableMode) => {
        // Both directions restart at page 1: an accumulated infinite list is
        // discarded, and a paged view returns to the top.
        setState((prev) => (prev.mode === mode ? prev : { ...prev, mode, page: 1 }));
    }, []);

    const reset = useCallback(() => {
        const { defaultSort, mode = DEFAULT_MODE } = optionsRef.current;
        setState({
            page: 1,
            limit: optionsRef.current.defaultLimit ?? DEFAULT_PAGED_LIMIT,
            sort: defaultSort?.by,
            order: defaultSort?.order,
            search: '',
            filters: defaultFiltersRef.current,
            mode
        });
    }, []);

    // Caller-supplied object literals again: compare by content, not identity,
    // or the persistence effect re-runs on every render.
    const filterStorageKeysRef = useRef(filterStorageKeys);
    filterStorageKeysRef.current = filterStorageKeys;
    const filterStorageSignature = JSON.stringify(filterStorageKeys ?? null);

    const { sort, order, filters, mode } = state;
    useEffect(() => {
        const ownKeys = filterStorageKeysRef.current;
        Object.entries(ownKeys ?? {}).forEach(([name, key]) => {
            writeStored(key, filters[name]);
        });
        if (!storageKey) {
            return;
        }
        const blobFilters = Object.fromEntries(
            Object.entries(filters).filter(([name]) => !(ownKeys && name in ownKeys))
        );
        writeStored(storageKey, {
            ...(sort !== undefined && order !== undefined ? { sort, order } : {}),
            filters: blobFilters,
            mode
        });
    }, [sort, order, filters, mode, storageKey, filterStorageSignature]);

    const limit = state.mode === 'infinite' ? infiniteLimit : state.limit;

    const params = useMemo<DataTableParams<K>>(
        () => ({
            ...compactParams(filters),
            page: state.page,
            limit,
            ...(sort !== undefined && order !== undefined ? { sort, order } : {}),
            ...(state.search ? { search: state.search } : {})
        }),
        [filters, state.page, limit, sort, order, state.search]
    );

    // The overloads guarantee callers only ever see the shape matching their
    // options; internally sort/order are `K | undefined`, which is neither
    // member of the union on its own - hence the one cast in this module.
    return {
        page: state.page,
        limit,
        sort,
        order,
        search: state.search,
        filters,
        mode: state.mode,
        params,
        setPage,
        setLimit,
        toggleSort,
        setSearch,
        setFilter,
        setMode,
        reset
    } as UseDataTableResult<K, F> | UseDataTableUnsortedResult<F>;
}
